import { memo, useCallback } from 'react'

import { Cancel } from '@mui/icons-material'
import { Box, TextField } from '@mui/material'
import { ImageBlob, ImageBlobInsert, ImageBlobInsertFormatEnum } from '@nativewaves/nw-cloud-sdk/content'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useShallow } from 'zustand/react/shallow'

import { useCloseHandler } from '@shared/components/MaterialUIEnhancements/Dialog'
import {
  useSubmissionProgressStore,
  selectSubmissionProgressSetters,
  SubmissionProgress,
  SubmissionButton,
} from '@shared/components/SubmissionProgress'
import { DialogFormContent } from '@shared/layouts'
import { useImageBlobCreateMutation } from 'hooks/mutations/content/ImageObject'
import { imageBlobsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images/Image/Blobs/Routing'

type FormStructure = ImageBlobInsert

type NewDialogProps = {
  onSuccess?: (data: ImageBlob) => void
  initialValue?: ImageBlob
}

const NewDialogRaw = ({ onSuccess, initialValue }: NewDialogProps) => {
  const { t } = useTranslation(['common', 'domain'])
  const handleClose = useCloseHandler()

  const { imageId } = imageBlobsOverviewRoute.useParams()

  const { setCurrentStep } = useSubmissionProgressStore(useShallow(selectSubmissionProgressSetters))

  const { mutate: createImageBlob, ...imageBlobCreateProps } = useImageBlobCreateMutation()

  const { handleSubmit, control } = useForm<FormStructure>({
    defaultValues: {
      ...initialValue,
      format: ImageBlobInsertFormatEnum.Jpeg,
      storeId: '1',
    },
  })

  const handleFormSubmit = useCallback(
    (data: FormStructure) => {
      setCurrentStep({ value: 10, description: t('domain:Workspace.Image.creatingImage') })

      createImageBlob(
        {
          imageId,
          imageBlobInsert: data,
        },
        {
          onSuccess: (data) => {
            setTimeout(() => {
              handleClose()

              if (onSuccess) {
                setTimeout(() => {
                  onSuccess(data)
                }, 300)
              }
            }, 700)
          },
          onError: () => {
            setCurrentStep({
              value: 100,
              description: t('domain:Workspace.somethingWentWrong'),
              icon: <Cancel fontSize="small" color="error" />,
            })
          },
        },
      )
    },
    [setCurrentStep, t, createImageBlob, handleClose, onSuccess, imageId],
  )

  return (
    <DialogFormContent
      onSubmit={handleSubmit(handleFormSubmit)}
      title={t('domain:Workspace.Image.addImage')}
      description={t('domain:Workspace.Image.imageCreateDescription')}
      SubmitProgressZone={
        <SubmissionProgress isSuccess={imageBlobCreateProps.isSuccess} isError={imageBlobCreateProps.isError} />
      }
      SaveButton={
        <SubmissionButton
          submitCondition={!imageBlobCreateProps.isSuccess && !imageBlobCreateProps.isError}
          successCondition={imageBlobCreateProps.isSuccess}
          errorCondition={imageBlobCreateProps.isError}
          loading={imageBlobCreateProps.isPending}
          disabled={imageBlobCreateProps.isPending || imageBlobCreateProps.isSuccess}
        />
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: 2, my: 2 }}>
        <Controller
          control={control}
          name="storeId"
          rules={{
            required: true,
          }}
          render={({ field, fieldState }) => (
            <TextField
              {...field}
              label={t('name')}
              required
              helperText={fieldState.error?.message}
              error={!!fieldState.error}
            />
          )}
        />
      </Box>
    </DialogFormContent>
  )
}

export const NewDialog = memo(NewDialogRaw)
